import React from 'react';
import {
    Box,
    Chip,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatDisplayDate } from '../../utils/formatDisplayDate';

const QB_GREEN = '#2ca01c';

export function isTransactionReconciled(transaction) {
    if (!transaction) return false;
    return Boolean(transaction.is_reconciled || transaction.reconciliation);
}

export function getIntegrationLabel(integrationType) {
    if (integrationType === 'quickbooks') {
        return i18n.t('app.reconciliation.integrationQuickBooks');
    }
    return integrationType || i18n.t('app.reconciliation.integrationExternal');
}

function formatAmount(value) {
    if (value == null || value === '') return '—';
    const n = parseFloat(String(value).replace(/,/g, ''));
    if (!Number.isFinite(n)) return value;
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export function TransactionReconciliationStatus({ transaction, size = 'small' }) {
    const { t } = useTranslation();
    const reconciled = isTransactionReconciled(transaction);
    const reconciliation = transaction?.reconciliation;
    const integrationLabel = getIntegrationLabel(reconciliation?.integration_type);

    if (!reconciled) {
        return (
            <Tooltip title={t('app.reconciliation.notReconciled')}>
                <Chip
                    icon={<LinkOffIcon />}
                    label={t('app.reconciliation.open')}
                    size={size}
                    variant="outlined"
                    sx={{ color: 'text.secondary', borderColor: 'divider' }}
                />
            </Tooltip>
        );
    }

    return (
        <Tooltip title={t('app.reconciliation.reconciledWith', { integration: integrationLabel })}>
            <Chip
                icon={<LinkIcon />}
                label={integrationLabel}
                size={size}
                sx={{
                    bgcolor: QB_GREEN,
                    color: '#fff',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: '#fff' },
                }}
            />
        </Tooltip>
    );
}

export function TransactionReconciliationDetails({ transaction }) {
    const { t, i18n: i18nInstance } = useTranslation();
    const reconciliation = transaction?.reconciliation;

    if (!isTransactionReconciled(transaction) || !reconciliation) {
        return (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('app.reconciliation.title')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TransactionReconciliationStatus transaction={transaction} />
                    <Typography variant="body2" color="text.secondary">
                        {t('app.reconciliation.notLinkedDescription')}
                    </Typography>
                </Stack>
            </Paper>
        );
    }

    const metadata = reconciliation.metadata || {};
    const integrationLabel = getIntegrationLabel(reconciliation.integration_type);
    const splits = metadata.splits || [];

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('app.reconciliation.title')}
                </Typography>
                <TransactionReconciliationStatus transaction={transaction} />
            </Stack>

            <Typography variant="overline" color="text.secondary">
                {integrationLabel}
            </Typography>

            <Stack spacing={0.75} sx={{ mt: 1 }}>
                <Typography variant="body2">
                    <strong>{metadata.transactionType || t('app.reconciliation.transaction')}</strong>
                    {metadata.num ? ` #${metadata.num}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('app.reconciliation.externalId')}: {reconciliation.external_transaction_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('app.reconciliation.date')}: {formatDisplayDate(metadata.date, i18nInstance.language)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('app.reconciliation.amount')}: {formatAmount(metadata.amount)}
                </Typography>
                {metadata.name && (
                    <Typography variant="body2" color="text.secondary">
                        {t('app.reconciliation.name')}: {metadata.name}
                    </Typography>
                )}
                {metadata.account && (
                    <Typography variant="body2" color="text.secondary">
                        {t('app.reconciliation.account')}: {metadata.account}
                    </Typography>
                )}
                {metadata.memo && (
                    <Typography variant="body2" color="text.secondary">
                        {t('app.reconciliation.memo')}: {metadata.memo}
                    </Typography>
                )}
            </Stack>

            {splits.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('app.reconciliation.splits')}
                    </Typography>
                    <Stack spacing={1}>
                        {splits.map((split, index) => (
                            <Box
                                key={`split-${index}`}
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Typography variant="body2">
                                    {split.account || '—'}
                                    {split.name ? ` · ${split.name}` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {split.memo || '—'} · {formatAmount(split.amount)}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Paper>
    );
}
